import { useContext, useState } from "react";
import { Alert, Button, Container, Form, Row, Spinner } from "react-bootstrap";
import ColM from "../../assets/components/column/ColM";

import CreateOrderProvider from "../../context/createOrder/Provider";
import createOrderContext from "../../context/createOrder/";
import { PricesProvider, usePrices } from "../../context/prices/PricesContext";
import { PricingProvider, usePricing } from "../../context/pricing/PricingContext";

import { Order } from "../../types/orderTypes";
import CustomerDetails from "../../components/createOrder/CustomerDetails";
import StairsType from "../../components/createOrder/StairsType";
import StairSteps from "../../components/createOrder/StairSteps";
import Risers from "../../components/createOrder/Risers";
import TopConnection from "../../components/createOrder/TopConnection";
import Extras from "../../components/createOrder/Extras";
import Assembly from "../../components/createOrder/Assembly";
import { Submission } from "../../types/authTypes";
import useAxios from "../../hooks/axios/useAxios";
import { baseUrl } from "../../config/serverConfig";

import { DefaultResponseType } from "../../types/axiosTypes";
import messageBox from "../../assets/components/message/MessageBox";
import { useNavigate } from "react-router-dom";

// Zelfde sectiekoppen/opbouw als de offerte + order-detail (feature 4).
const SectionTitle = ({ children }: { children: string }) => (
  <h6
    style={{
      fontWeight: 600,
      fontSize: "0.8rem",
      textTransform: "uppercase",
      letterSpacing: "0.06em",
      color: "#6c757d",
      borderBottom: "1px solid #e9ecef",
      paddingBottom: 6,
      marginBottom: 16,
      marginTop: 12,
    }}
  >
    {children}
  </h6>
);

const SubOrder = () => {
  const navigate = useNavigate();
  const order = useContext(createOrderContext);
  const { stairAmount, phone } = order;

  // Zonder prijzen en kleuren uit /prices kan er geen geldige order worden
  // samengesteld; de backend zou de kleurcode toch weigeren.
  const { loadError } = usePrices();

  // De prijs komt volledig van de server. Zolang die er niet is, mag er geen
  // offerte worden opgeslagen — er zou dan een order ontstaan zonder dat
  // iemand het bedrag heeft gezien.
  const {
    pricing,
    loading: pricingLoading,
    error: pricingError,
    config,
  } = usePricing();

  const { fetchData } = useAxios({ method: "post" });

  // Opslaan is een schrijfactie die een offerte aanmaakt; twee keer klikken
  // levert twee offertes op. De knop blijft daarom ook tijdens het verzoek
  // zelf geblokkeerd, niet alleen zolang er nog geen prijs is.
  const [bezig, setBezig] = useState(false);

  const opslaanGeblokkeerd =
    bezig || !!loadError || !!pricingError || pricingLoading || !pricing;

  const submit = async (event: Submission) => {
    event.preventDefault();

    // De browser kan het formulier ook met Enter versturen, langs de
    // uitgeschakelde knop heen.
    if (bezig) return;

    if (loadError) {
      return messageBox({
        message:
          "Opslaan is geblokkeerd: de prijzen en kleuren zijn niet geladen.",
        type: "error",
      });
    }

    if (pricingError || !pricing) {
      return messageBox({
        message:
          pricingError ?? "Opslaan is geblokkeerd: er is nog geen prijs berekend.",
        type: "error",
      });
    }

    function containsAnyLetter(str: string) {
      return /[a-zA-Z]/.test(str);
    }

    if (containsAnyLetter(phone.value)) {
      return messageBox({
        message: "Telefonnummer is onjuist",
        type: "error",
      });
    }

    // Alleen de configuratie en de klantgegevens. Bedragen worden bewust niet
    // meegestuurd: de server rekent zelf en negeert ze toch.
    const body = {
      ...config,
      first_name: order.first_name.value,
      last_name: order.last_name.value,
      email: order.email.value,
      phone: order.phone.value,
      street: order.street.value,
      house_number: order.house_number.value,
      zipcode: order.zipcode.value,
      city: order.city.value,
    };

    setBezig(true);
    const response: DefaultResponseType<Order> = await fetchData(
      `${baseUrl}/order/create-order`,
      body
    );

    if (response.error) {
      setBezig(false);
      // Bij een 4xx/5xx staat in `message` de axios-tekst ("Request failed
      // with status code 400"); de melding van de server zit in `data`, die
      // dan de foutbody is en geen Order.
      const foutbody = response.data as unknown as { message?: string } | undefined;
      messageBox({
        message:
          foutbody?.message ??
          response.message ??
          "Fout tijdens het aanmaken van order",
        type: "error",
      });
    } else {
      messageBox({ message: "Order aangemaakt", type: "success" });
      navigate("/orders");
    }
  };

  return (
    <Container fluid>
      {loadError && (
        <Alert variant="danger" className="mt-3">
          <Alert.Heading style={{ fontSize: "1rem" }}>
            Configurator niet beschikbaar
          </Alert.Heading>
          <div>{loadError}</div>
          <div className="mt-2 fw-semibold">Je kunt nu geen offerte aanmaken.</div>
        </Alert>
      )}

      <Form onSubmit={submit}>
        <Row>
          <ColM xs={12}>
            <SectionTitle>Klantgegevens</SectionTitle>
            <CustomerDetails />
          </ColM>
        </Row>

        <Row>
          <ColM xs={12}>
            <SectionTitle>Trap specificatie</SectionTitle>
            <StairsType />
          </ColM>
        </Row>

        <Row>
          <ColM xs={12}>
            <StairSteps />
          </ColM>
        </Row>

        {stairAmount.value > 0 && (
          <>
            <Row>
              <ColM xs={12}>
                <Risers />
              </ColM>
            </Row>

            <Row>
              <ColM xs={12}>
                <TopConnection />
              </ColM>
            </Row>

            <Row>
              <ColM xs={12}>
                <SectionTitle>Extra's</SectionTitle>
              </ColM>
              <Extras />
            </Row>

            <Row>
              <ColM xs={12}>
                <SectionTitle>Prijsoverzicht</SectionTitle>
              </ColM>
              <Assembly />
            </Row>

            <Row>
              <ColM xs={12}>
                <div className="d-flex justify-content-end align-items-center gap-3">
                  {pricingLoading && (
                    <span className="text-muted" style={{ fontSize: "0.85rem" }}>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Prijs berekenen…
                    </span>
                  )}
                  <Button type="submit" disabled={opslaanGeblokkeerd}>
                    {bezig ? "Bezig met opslaan…" : "Offerte aanmaken"}
                  </Button>
                </div>
              </ColM>
            </Row>
          </>
        )}
      </Form>
    </Container>
  );
};

const SuperOrder = () => {
  return (
    <PricesProvider>
      <CreateOrderProvider>
        <PricingProvider>
          <SubOrder />
        </PricingProvider>
      </CreateOrderProvider>
    </PricesProvider>
  );
};

export default SuperOrder;
